/**
 * <LineItemsGrid>
 * Spreadsheet-style editable grid for invoice / quotation / sale line items.
 *
 * Designed to be reusable across CreateInvoice, RecordSale, future Purchase
 * Orders, Sales Orders, Delivery Notes, etc.
 *
 * Public API (props):
 *   value        : LineItem[] — controlled. Each item: { _uid?, product_id, product_name, sku,
 *                                                       quantity, unit_price, line_total, vat_rate,
 *                                                       batch_required?, batch_allocations?, ... }
 *   onChange     : (LineItem[]) => void
 *   products     : Product[]
 *   currencyFmt  : (n) => string
 *   onSubmit?    : () => void — wired to Ctrl+S
 *   renderRowExtra?: (line, idx) => ReactNode — secondary row (e.g. BatchPicker)
 *   autoFocusFirst : boolean — focus the first row's product on mount (default true)
 *
 * Behavior:
 *   - Tab / Shift+Tab → native browser order through cells (product → qty → price → next row)
 *   - Enter inside product (with selection committed via combobox) → focus quantity
 *   - Enter inside qty → focus price
 *   - Enter inside price → if last row, append a new row; focus next product
 *   - ArrowUp / ArrowDown → move to same column on previous / next row
 *   - Ctrl+D → duplicate the active row
 *   - Ctrl+Delete / Cmd+Backspace → remove the active row
 *   - Alt+N → append a new empty row, focus product
 *   - Ctrl+S → call onSubmit (if provided)
 *   - Enter is preventDefault'd everywhere inside the grid so the parent <form>
 *     does NOT accidentally submit.
 *
 * Performance:
 *   - Each line has a stable `_uid` (assigned lazily) so React reconciles by uid
 *     and re-renders stay localised when a single cell changes.
 *   - `LineRow` is memoised with `areEqual` checking by uid + product list identity.
 */
import {
    Fragment, memo, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Copy } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import ProductCombobox from './ProductCombobox';

const COL_ORDER = ['product', 'qty', 'price'];
const newUid = () => `li_${Math.random().toString(36).slice(2, 10)}`;

const blankRow = () => ({
    _uid: newUid(),
    product_id: '', product_name: '', sku: '',
    quantity: 1, unit_price: 0, line_total: 0,
    vat_rate: 20,
    batch_required: false, batch_allocations: null,
});

// Tag any incoming rows with a stable _uid if they don't have one yet.
const ensureUids = (rows) => rows.map((r) => (r._uid ? r : { ...r, _uid: newUid() }));

const LineItemsGrid = ({
    value,
    onChange,
    products,
    currencyFmt = (n) => Number(n || 0).toFixed(2),
    onSubmit,
    renderRowExtra,
    autoFocusFirst = true,
    testId = 'line-items-grid',
}) => {
    const { t } = useTranslation(['entities', 'common']);

    // Internal ref-based focus management. Keys are `${rowIdx}:${col}`.
    const cellRefs = useRef({});
    const [activeRow, setActiveRow] = useState(0);
    const didAutoFocusRef = useRef(false);

    // Detect desktop (fine pointer) — keyboard shortcuts only attach there.
    const isDesktop = useMemo(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return true;
        return window.matchMedia('(pointer: fine)').matches;
    }, []);

    const registerCell = useCallback((row, col, el) => {
        const k = `${row}:${col}`;
        if (el) cellRefs.current[k] = el;
        else delete cellRefs.current[k];
    }, []);

    const focusCell = useCallback((row, col) => {
        // Wait for React to flush the new row, then focus.
        requestAnimationFrame(() => {
            const el = cellRefs.current[`${row}:${col}`];
            if (!el) return;
            if (typeof el.focus === 'function') el.focus();
            // For native <input>, also select existing text so the user can type over.
            if (typeof el.select === 'function') {
                try { el.select(); } catch { /* unsupported on some elements */ }
            }
        });
    }, []);

    // Ensure rows always have a _uid (without mutating parent input).
    const rows = useMemo(() => ensureUids(value || []), [value]);

    // Mutators ----------------------------------------------------------------
    const replaceRows = useCallback((next) => {
        onChange?.(next);
    }, [onChange]);

    const updateRow = useCallback((idx, patch) => {
        const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
        // re-derive line_total
        const r = next[idx];
        const q = parseFloat(r.quantity) || 0;
        const u = parseFloat(r.unit_price) || 0;
        next[idx] = { ...r, line_total: +(q * u).toFixed(2) };
        replaceRows(next);
    }, [rows, replaceRows]);

    const selectProduct = useCallback((idx, product) => {
        const next = rows.map((r, i) => {
            if (i !== idx) return r;
            const u = Number(product.price) || 0;
            const q = parseFloat(r.quantity) || 1;
            return {
                ...r,
                product_id: product.id,
                product_name: product.name,
                sku: product.sku,
                unit_price: u,
                quantity: q || 1,
                vat_rate: product.vat_rate != null ? Number(product.vat_rate) : 20,
                batch_required: !!product.batch_required,
                batch_allocations: null,  // recompute via FEFO
                line_total: +(q * u).toFixed(2),
            };
        });
        replaceRows(next);
        focusCell(idx, 'qty');
    }, [rows, replaceRows, focusCell]);

    const addRow = useCallback((focusIdx) => {
        const next = [...rows, blankRow()];
        replaceRows(next);
        const newIdx = next.length - 1;
        focusCell(focusIdx ?? newIdx, 'product');
    }, [rows, replaceRows, focusCell]);

    const duplicateRow = useCallback((idx) => {
        const src = rows[idx];
        if (!src) return;
        const dup = { ...src, _uid: newUid(), batch_allocations: null };
        const next = [...rows.slice(0, idx + 1), dup, ...rows.slice(idx + 1)];
        replaceRows(next);
        focusCell(idx + 1, 'product');
    }, [rows, replaceRows, focusCell]);

    const removeRow = useCallback((idx) => {
        const next = rows.filter((_, i) => i !== idx);
        replaceRows(next);
        // refocus a sensible neighbour
        const nextIdx = Math.max(0, Math.min(idx, next.length - 1));
        if (next.length > 0) focusCell(nextIdx, 'product');
    }, [rows, replaceRows, focusCell]);

    // Auto-focus first row product on mount (and create one if grid is empty) --
    useEffect(() => {
        if (!autoFocusFirst || didAutoFocusRef.current) return;
        didAutoFocusRef.current = true;
        if (rows.length === 0) {
            replaceRows([blankRow()]);
        }
        focusCell(0, 'product');
    }, []);

    // Self-healing: if the user empties the grid (e.g. removes the last row),
    // re-seed it with a blank row so they're never stuck without an input to focus.
    useEffect(() => {
        if (!autoFocusFirst) return;
        if (rows.length === 0) replaceRows([blankRow()]);
    }, [rows.length, autoFocusFirst, replaceRows]);

    // Cell-level key handler --------------------------------------------------
    const onCellKeyDown = useCallback((row, col, e) => {
        // Block Enter from bubbling into the host <form>.
        if (e.key === 'Enter' && !e.shiftKey) {
            // Combobox handles its own Enter when a result is highlighted;
            // we only get here when nothing is highlighted OR for qty/price cells.
            e.preventDefault();
            if (col === 'product') {
                focusCell(row, 'qty');
                return;
            }
            if (col === 'qty') {
                focusCell(row, 'price');
                return;
            }
            if (col === 'price') {
                if (row === rows.length - 1) {
                    addRow(row + 1);
                } else {
                    focusCell(row + 1, 'product');
                }
                return;
            }
        }
        if (e.key === 'ArrowDown' && (col === 'qty' || col === 'price')) {
            e.preventDefault();
            if (row < rows.length - 1) focusCell(row + 1, col);
            return;
        }
        if (e.key === 'ArrowUp' && (col === 'qty' || col === 'price')) {
            e.preventDefault();
            if (row > 0) focusCell(row - 1, col);
            return;
        }
    }, [rows.length, focusCell, addRow]);

    // Global shortcuts (desktop only) -----------------------------------------
    useEffect(() => {
        if (!isDesktop) return;
        const handler = (e) => {
            const mod = e.ctrlKey || e.metaKey;
            // Ctrl+S is allowed anywhere on the page (act on the host form).
            if (mod && e.key.toLowerCase() === 's' && onSubmit) {
                e.preventDefault();
                onSubmit();
                return;
            }
            // Alt+N adds a row regardless of where focus is on the page —
            // useful when the user just landed and hasn't tabbed into the grid yet.
            if (e.altKey && e.key.toLowerCase() === 'n') {
                e.preventDefault();
                addRow();
                return;
            }
            // Ctrl+D and Ctrl+Delete operate on the active row, so they require
            // focus inside the grid (otherwise we don't know which row).
            const root = document.querySelector(`[data-grid-root="${testId}"]`);
            const insideGrid = root && root.contains(document.activeElement);
            if (!insideGrid) return;
            if (mod && e.key.toLowerCase() === 'd') {
                e.preventDefault();
                duplicateRow(activeRow);
                return;
            }
            if (mod && (e.key === 'Delete' || e.key === 'Backspace')) {
                e.preventDefault();
                removeRow(activeRow);
                return;
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isDesktop, activeRow, addRow, duplicateRow, removeRow, onSubmit, testId]);

    // Helper for focus tracking
    const markActive = (idx) => () => setActiveRow(idx);

    // ------------------------------------------------------------------------
    return (
        <div
            data-grid-root={testId}
            data-testid={testId}
            // Final defence: any Enter inside the grid that escaped a cell handler
            // must not submit the parent <form>.
            onKeyDownCapture={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && e.target?.tagName !== 'BUTTON') {
                    e.preventDefault();
                }
            }}
        >
            <div className="w-full overflow-x-auto -mx-2 md:mx-0 px-2 md:px-0">
                <Table className="min-w-[520px]">
                    <TableHeader>
                        <TableRow>
                            <TableHead style={{ width: '40%' }}>{t('entities:products.columns.name')}</TableHead>
                            <TableHead className="text-right">{t('entities:products.columns.stock', { defaultValue: 'Qty' })}</TableHead>
                            <TableHead className="text-right">{t('entities:products.dialog.price')}</TableHead>
                            <TableHead className="text-right">{t('entities:reports.columns.amount')}</TableHead>
                            <TableHead className="w-[64px]"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((li, idx) => (
                            <Fragment key={li._uid}>
                                <LineRow
                                    idx={idx}
                                    line={li}
                                    products={products}
                                    currencyFmt={currencyFmt}
                                    isLast={idx === rows.length - 1}
                                    onSelectProduct={(p) => selectProduct(idx, p)}
                                    onUpdate={(patch) => updateRow(idx, patch)}
                                    onDuplicate={() => duplicateRow(idx)}
                                    onRemove={() => removeRow(idx)}
                                    onCellKeyDown={onCellKeyDown}
                                    onFocus={markActive(idx)}
                                    registerCell={registerCell}
                                />
                                {renderRowExtra && renderRowExtra(li, idx)}
                            </Fragment>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
                <Button
                    type="button"
                    onClick={() => addRow()}
                    size="sm"
                    data-testid="add-line-item"
                    variant="outline"
                >
                    <Plus size={14} className="mr-1" /> {t('common:actions.add', { defaultValue: 'Add Item' })}
                </Button>
                {isDesktop && (
                    <p className="text-[10px] text-gray-400 hidden md:block">
                        <kbd className="px-1.5 py-0.5 rounded bg-[#F5F5F5] border border-[#E5E5E5] font-mono">Tab</kbd>{' '}/{' '}
                        <kbd className="px-1.5 py-0.5 rounded bg-[#F5F5F5] border border-[#E5E5E5] font-mono">Enter</kbd>{' '}
                        {t('common:lineitems.kb_nav', { defaultValue: 'move' })}{' · '}
                        <kbd className="px-1.5 py-0.5 rounded bg-[#F5F5F5] border border-[#E5E5E5] font-mono">Alt+N</kbd>{' '}
                        {t('common:lineitems.kb_add', { defaultValue: 'add row' })}{' · '}
                        <kbd className="px-1.5 py-0.5 rounded bg-[#F5F5F5] border border-[#E5E5E5] font-mono">Ctrl+D</kbd>{' '}
                        {t('common:lineitems.kb_dup', { defaultValue: 'duplicate' })}{' · '}
                        <kbd className="px-1.5 py-0.5 rounded bg-[#F5F5F5] border border-[#E5E5E5] font-mono">Ctrl+Del</kbd>{' '}
                        {t('common:lineitems.kb_del', { defaultValue: 'remove' })}
                    </p>
                )}
            </div>
        </div>
    );
};

// -------------------------------------------------------------------------
// Memoised row — only re-renders when this row's data changes.
// -------------------------------------------------------------------------
const LineRow = memo(function LineRow({
    idx, line, products, currencyFmt, isLast,
    onSelectProduct, onUpdate, onDuplicate, onRemove,
    onCellKeyDown, onFocus, registerCell,
}) {
    const productRef = useRef(null);

    // Register the combobox imperative handle as the cell focus target.
    useEffect(() => {
        registerCell(idx, 'product', productRef.current);
        return () => registerCell(idx, 'product', null);
    }, [idx, registerCell]);

    return (
        <TableRow data-testid={`line-item-${idx}`} className="align-top">
            <TableCell className="py-2">
                <ProductCombobox
                    ref={productRef}
                    products={products}
                    value={line.product_id}
                    onSelect={onSelectProduct}
                    onEnterEmpty={() => onCellKeyDown(idx, 'product', { key: 'Enter', preventDefault: () => {} })}
                    testId={`line-product-${idx}`}
                />
            </TableCell>
            <TableCell className="py-2 text-right">
                <Input
                    ref={(el) => registerCell(idx, 'qty', el)}
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    data-testid={`line-qty-${idx}`}
                    value={line.quantity}
                    onFocus={onFocus}
                    onChange={(e) => onUpdate({ quantity: e.target.value })}
                    onKeyDown={(e) => onCellKeyDown(idx, 'qty', e)}
                    className="h-9 w-24 text-right ml-auto"
                />
            </TableCell>
            <TableCell className="py-2 text-right">
                <Input
                    ref={(el) => registerCell(idx, 'price', el)}
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    data-testid={`line-price-${idx}`}
                    value={line.unit_price}
                    onFocus={onFocus}
                    onChange={(e) => onUpdate({ unit_price: e.target.value })}
                    onKeyDown={(e) => onCellKeyDown(idx, 'price', e)}
                    className="h-9 w-28 text-right ml-auto"
                />
            </TableCell>
            <TableCell className="py-2 text-right font-mono font-semibold whitespace-nowrap">
                {currencyFmt(line.line_total)}
            </TableCell>
            <TableCell className="py-2 text-right">
                <div className="flex items-center justify-end gap-0.5">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={onDuplicate}
                        data-testid={`duplicate-line-${idx}`}
                        title="Ctrl+D"
                        className="h-7 w-7 text-gray-400 hover:text-[#264653]"
                    >
                        <Copy size={13} />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={onRemove}
                        data-testid={`remove-line-${idx}`}
                        title="Ctrl+Del"
                        className="h-7 w-7 text-gray-400 hover:text-[#E76F51]"
                    >
                        <Trash2 size={13} />
                    </Button>
                </div>
            </TableCell>
        </TableRow>
    );
});

export default LineItemsGrid;
